import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_BUSINESS,
  PREMISES_KEYS,
  PRICE_CHOICES,
  PRICE_KEYS,
  buyPricePence,
  businessEstimate,
  formatPence,
  setupPence,
  weeklyRentPence,
  type BusinessAction,
  type BusinessView,
  type BusinessesReply,
  type PremisesKey,
  type Tenure,
} from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { useCatalogue } from '../lib/content';
import { ShelfPanel } from './shelf-panel';
import { actionKey, londonWhen } from '../lib/format';

type Props = {
  onClose: () => void;
  // the wallet changed (started, cashed out, paid, sold)
  onWallet: () => void;
};
const TIER = ['', '£', '££', '£££', '££££'];

// Business, as Lagos Life's Company: your businesses and their tills, starting one (what, where, how big, rent
// or buy), and running one (cash out, staff, prices, rent owed, selling it back). The books are worked out up
// to now each time this opens.
export function BusinessScreen({ onClose, onWallet }: Props) {
  const content = useCatalogue()?.business ?? DEFAULT_BUSINESS;
  const [reply, setReply] = useState<BusinessesReply | null>(null);
  const [failure, setFailure] = useState<ReturnType<typeof describeError> | null>(null);
  const [view, setView] = useState<{ kind: 'list' } | { kind: 'start' } | { kind: 'one'; id: string }>({
    kind: 'list',
  });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      setReply(await api.business.list());
      setFailure(null);
    } catch (error) {
      setFailure(describeError(error));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  // a change: the reply has the businesses and the wallet as they are now
  const run = async (work: () => Promise<BusinessesReply & { message?: string }>, money = false) => {
    setBusy(true);
    try {
      const next = await work();
      setReply(next);
      if (next.message) notify(next.message);
      if (money) onWallet();
      return true;
    } catch (error) {
      notify(describeError(error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const open = view.kind === 'one' ? reply?.businesses.find((item) => item.id === view.id) : undefined;
  const back =
    view.kind === 'list' ? (
      <button type="button" className="profile-back" onClick={onClose}>
        ‹ Phone
      </button>
    ) : (
      <button type="button" className="profile-back" onClick={() => setView({ kind: 'list' })}>
        ‹ Business
      </button>
    );
  return (
    <div className="profile-screen business-screen" role="dialog" aria-modal="true" aria-labelledby="business-title">
      <div className="profile-frame">
        <header className="profile-header">
          {back}
          <h2 id="business-title">{view.kind === 'start' ? 'Start a business' : open ? open.name : '🏢 Business'}</h2>
          <button type="button" className="life-close" aria-label="Close Business" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="profile-body">
          {failure && <ErrorNotice error={failure} onRetry={() => void load()} />}
          {!reply && !failure && <p className="business-muted">Opening the books…</p>}
          {reply && view.kind === 'list' && (
            <BusinessList
              reply={reply}
              content={content}
              onOpen={(id) => setView({ kind: 'one', id })}
              onStart={() => setView({ kind: 'start' })}
            />
          )}
          {reply && view.kind === 'start' && (
            <StartBusiness
              content={content}
              walletPence={reply.walletPence}
              busy={busy}
              onStart={async (body, key) => {
                const ok = await run(() => api.business.start(body, key), true);
                if (ok) setView({ kind: 'list' });
              }}
            />
          )}
          {reply && open && (
            <BusinessDetail
              business={open}
              content={content}
              walletPence={reply.walletPence}
              busy={busy}
              onAct={(body, money) =>
                run(() => api.business.act(open.id, body, money ? actionKey('business') : undefined), money)
              }
              onSold={() => setView({ kind: 'list' })}
            />
          )}
          {reply && view.kind === 'one' && !open && <p className="business-muted">That business has closed.</p>}
        </div>
      </div>
    </div>
  );
}

type Content = typeof DEFAULT_BUSINESS;
const kindOf = (content: Content, id: string) => content.kinds.find((item) => item.id === id);
const areaOf = (content: Content, id: string) => content.areas.find((item) => item.id === id);

function BusinessList({
  reply,
  content,
  onOpen,
  onStart,
}: {
  reply: BusinessesReply;
  content: Content;
  onOpen: (id: string) => void;
  onStart: () => void;
}) {
  const tills = reply.businesses.reduce((sum, item) => sum + item.tillPence, 0);
  const full = reply.businesses.length >= content.rules.maxBusinesses;
  return (
    <div className="business-list">
      <section className="business-hero" aria-label="Your tills">
        <small>In your tills</small>
        <strong>{formatPence(tills)}</strong>
        <span>
          {reply.businesses[0]
            ? `Takings land here every evening · next at ${londonWhen(reply.businesses[0].nextEveningAt)}`
            : 'City customers come every evening at 6 pm; the takings land in the till'}
        </span>
      </section>
      {reply.businesses.length === 0 ? (
        <p className="business-muted">
          Start a business: a café, a barber, a corner shop… Rent the premises by the week or buy them outright. Bills
          (rent, wages and {Math.round(content.rules.tax * 100)}% tax) come out of the till on Saturday at 6 pm.
        </p>
      ) : (
        <ul className="business-cards">
          {reply.businesses.map((item) => {
            const kind = kindOf(content, item.kind);
            return (
              <li key={item.id}>
                <button type="button" onClick={() => onOpen(item.id)} aria-label={`Open ${item.name}`}>
                  <span className="business-icon" aria-hidden="true">
                    {kind?.icon ?? '🏢'}
                  </span>
                  <span className="business-who">
                    <strong>{item.name}</strong>
                    <small>
                      {kind?.label ?? item.kind} · {areaOf(content, item.area)?.name ?? item.area} ·{' '}
                      {content.premises[item.size].label}
                    </small>
                    <small>
                      {item.lastEvening
                        ? `Last evening ${formatPence(item.lastEvening.takingsPence)} · ${item.lastEvening.customers} customers`
                        : 'First customers at 6 pm'}
                    </small>
                  </span>
                  <span className="business-till">
                    <em className={`business-status ${item.status}`}>{item.status === 'open' ? 'Open' : 'Shut'}</em>
                    {formatPence(item.tillPence)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {!content.rules.open ? (
        <p className="business-muted">Starting a business is paused for now.</p>
      ) : (
        <button type="button" className="city-card-go" disabled={full} onClick={onStart}>
          {full ? `You run ${content.rules.maxBusinesses} businesses, the most at a time` : '➕ Start a business'}
        </button>
      )}
    </div>
  );
}

function StartBusiness({
  content,
  walletPence,
  busy,
  onStart,
}: {
  content: Content;
  walletPence: number;
  busy: boolean;
  onStart: (
    body: { name: string; kind: string; area: string; size: PremisesKey; tenure: Tenure },
    key: string,
  ) => Promise<void>;
}) {
  const kinds = content.kinds.filter((item) => item.visible);
  const [kindId, setKindId] = useState(kinds[0]?.id ?? '');
  const [areaId, setAreaId] = useState(content.areas.find((item) => item.tier === 2)?.id ?? content.areas[0]!.id);
  const [size, setSize] = useState<PremisesKey>('stall');
  const [tenure, setTenure] = useState<Tenure>('rent');
  const [name, setName] = useState('');
  const attempt = useRef(actionKey('business'));
  const kind = kindOf(content, kindId);
  const area = areaOf(content, areaId);
  const plan = kind && area ? { kind, area, size } : null;
  const sums = (() => {
    if (!plan) return null;
    const setup = setupPence(content, plan);
    const premises = tenure === 'own' ? buyPricePence(content, plan) : 0;
    return {
      setup,
      premises,
      total: content.rules.registerPence + setup + premises,
      rent: weeklyRentPence(content, plan),
      buy: buyPricePence(content, plan),
      estimate: businessEstimate(content, plan),
    };
  })();
  if (!plan || !sums) return <p className="business-muted">No businesses can be started right now.</p>;
  const short = walletPence < sums.total;
  const bills = sums.estimate.weekWagesPence + sums.estimate.weekTaxPence + (tenure === 'rent' ? sums.rent : 0);
  return (
    <div className="business-start">
      <fieldset className="business-step">
        <legend>1 · What</legend>
        <div className="business-kinds" role="radiogroup" aria-label="Kind of business">
          {kinds.map((item) => (
            <button
              type="button"
              key={item.id}
              role="radio"
              aria-checked={kindId === item.id}
              className={kindId === item.id ? 'active' : ''}
              onClick={() => setKindId(item.id)}
            >
              <span aria-hidden="true">{item.icon}</span>
              <strong>{item.label}</strong>
              <small>{item.blurb}</small>
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset className="business-step">
        <legend>2 · Where</legend>
        <div className="business-chips" role="radiogroup" aria-label="Area">
          {content.areas.map((item) => (
            <button
              type="button"
              key={item.id}
              role="radio"
              aria-checked={areaId === item.id}
              className={areaId === item.id ? 'active' : ''}
              onClick={() => setAreaId(item.id)}
            >
              {item.name} <small>{TIER[item.tier]}</small>
            </button>
          ))}
        </div>
        <small className="business-muted">Dearer areas cost more and bring more, and bigger spenders.</small>
      </fieldset>
      <fieldset className="business-step">
        <legend>3 · How big</legend>
        <div className="business-options" role="radiogroup" aria-label="Premises">
          {PREMISES_KEYS.map((key) => (
            <button
              type="button"
              key={key}
              role="radio"
              aria-checked={size === key}
              className={size === key ? 'active' : ''}
              onClick={() => setSize(key)}
            >
              <span aria-hidden="true">{content.premises[key].icon}</span>
              <strong>{content.premises[key].label}</strong>
              <small>Fit-out {formatPence(setupPence(content, { ...plan, size: key }))}</small>
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset className="business-step">
        <legend>4 · Rent or buy the premises</legend>
        <div className="business-options" role="radiogroup" aria-label="Rent or buy">
          <button
            type="button"
            role="radio"
            aria-checked={tenure === 'rent'}
            className={tenure === 'rent' ? 'active' : ''}
            onClick={() => setTenure('rent')}
          >
            <span aria-hidden="true">🔑</span>
            <strong>Rent</strong>
            <small>{formatPence(sums.rent)} a week</small>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={tenure === 'own'}
            className={tenure === 'own' ? 'active' : ''}
            onClick={() => setTenure('own')}
          >
            <span aria-hidden="true">🏠</span>
            <strong>Buy</strong>
            <small>{formatPence(sums.buy)} once, no rent</small>
          </button>
        </div>
      </fieldset>
      <fieldset className="business-step">
        <legend>5 · Its name</legend>
        <label className="advert-field">
          <span>Business name</span>
          <input value={name} maxLength={30} onChange={(event) => setName(event.target.value)} />
        </label>
      </fieldset>
      <section className="business-sums" aria-label="The sums">
        <p>
          <span>Register</span> <strong>{formatPence(content.rules.registerPence)}</strong>
        </p>
        <p>
          <span>Fit-out</span> <strong>{formatPence(sums.setup)}</strong>
        </p>
        {tenure === 'own' && (
          <p>
            <span>The premises</span> <strong>{formatPence(sums.premises)}</strong>
          </p>
        )}
        <p className="total">
          <span>Now</span> <strong>{formatPence(sums.total)}</strong>
        </p>
        <small>
          Makes about {formatPence(sums.estimate.nightPence)} an evening with {sums.estimate.staff.usual} staff at fair
          prices. Bills about {formatPence(bills)} a week. Sold back, it pays {Math.round(content.rules.resale * 100)}%
          of what it cost.
        </small>
      </section>
      <button
        type="button"
        className="city-card-go"
        disabled={busy || short || name.trim().length < 3}
        onClick={() =>
          void onStart({ name: name.trim(), kind: kindId, area: areaId, size, tenure }, attempt.current).then(() => {
            attempt.current = actionKey('business');
          })
        }
      >
        {short ? `You need ${formatPence(sums.total)}` : `Start for ${formatPence(sums.total)}`}
      </button>
    </div>
  );
}

function BusinessDetail({
  business,
  content,
  walletPence,
  busy,
  onAct,
  onSold,
}: {
  business: BusinessView;
  content: Content;
  walletPence: number;
  busy: boolean;
  onAct: (body: BusinessAction, money: boolean) => Promise<boolean>;
  onSold: () => void;
}) {
  const [selling, setSelling] = useState(false);
  const kind = kindOf(content, business.kind);
  const area = areaOf(content, business.area);
  const range = kind && area ? businessEstimate(content, { kind, area, size: business.size }).staff : null;
  const wage = business.wagePence;
  return (
    <div className="business-detail">
      <section className="business-hero" aria-label="The till">
        <small>
          {kind?.icon} {kind?.label} · {area?.name} · {content.premises[business.size].label} ·{' '}
          {business.tenure === 'own' ? 'owned' : `rented, ${formatPence(business.weeklyRentPence)} a week`}
        </small>
        <strong>{formatPence(business.tillPence)}</strong>
        <span>In the till</span>
        <button
          type="button"
          className="city-card-go"
          disabled={busy || business.tillPence <= 0}
          onClick={() => void onAct({ action: 'cash_out' }, true)}
        >
          {business.tillPence > 0 ? `Cash out ${formatPence(business.tillPence)}` : 'Nothing to cash out'}
        </button>
      </section>
      {business.owedPence > 0 && (
        <section className={`business-warning${business.status === 'shut' ? ' shut' : ''}`} role="alert">
          <strong>
            {business.status === 'shut'
              ? `🔒 Shut for ${formatPence(business.owedPence)} owed`
              : `⚠️ ${formatPence(business.owedPence)} owed`}
          </strong>
          <span>
            {business.status === 'shut'
              ? 'No customers until it is paid.'
              : `The takings pay it first; it shuts ${business.graceUntil ? londonWhen(business.graceUntil) : 'soon'} if any is still owed.`}
          </span>
          <button
            type="button"
            disabled={busy || walletPence < business.owedPence}
            onClick={() => void onAct({ action: 'pay_owed' }, true)}
          >
            {walletPence < business.owedPence
              ? `You need ${formatPence(business.owedPence)}`
              : `Pay ${formatPence(business.owedPence)} from your wallet`}
          </button>
        </section>
      )}
      <section className="business-stats" aria-label="This week">
        <p>
          <small>Last evening</small>
          <strong>{business.lastEvening ? formatPence(business.lastEvening.takingsPence) : '—'}</strong>
          <span>
            {business.lastEvening ? `${business.lastEvening.customers} customers` : 'First customers at 6 pm'}
          </span>
        </p>
        <p>
          <small>This week</small>
          <strong>{formatPence(business.weekTakingsPence)}</strong>
          <span>Next evening {londonWhen(business.nextEveningAt)}</span>
        </p>
        <p>
          <small>Bills</small>
          <strong>{formatPence(business.billsPence)}</strong>
          <span>{londonWhen(business.nextBillsAt)}</span>
        </p>
      </section>
      <section className="business-row" aria-label="Staff">
        <div>
          <strong>Staff</strong>
          <small>
            {formatPence(wage)} a week each{range ? ` · usual ${range.usual}, up to ${range.max}` : ''}
          </small>
        </div>
        <div className="business-stepper">
          <button
            type="button"
            aria-label="One fewer"
            disabled={busy || !range || business.staff <= range.min}
            onClick={() => void onAct({ action: 'staff', staff: business.staff - 1 }, false)}
          >
            −
          </button>
          <output aria-label="Staff">{business.staff}</output>
          <button
            type="button"
            aria-label="One more"
            disabled={busy || !range || business.staff >= range.max}
            onClick={() => void onAct({ action: 'staff', staff: business.staff + 1 }, false)}
          >
            +
          </button>
        </div>
      </section>
      <section className="business-row" aria-label="Prices">
        <div>
          <strong>Prices</strong>
          <small>Cheap brings more customers; premium, bigger spends</small>
        </div>
        <div className="business-chips" role="radiogroup" aria-label="Prices">
          {PRICE_KEYS.map((key) => (
            <button
              type="button"
              key={key}
              role="radio"
              aria-checked={business.price === key}
              className={business.price === key ? 'active' : ''}
              disabled={busy}
              onClick={() => void onAct({ action: 'price', price: key }, false)}
            >
              {PRICE_CHOICES[key].icon} {PRICE_CHOICES[key].label}
            </button>
          ))}
        </div>
      </section>
      {business.shelf && (
        <ShelfPanel business={business} content={content} walletPence={walletPence} busy={busy} onAct={onAct} />
      )}
      <section className="profile-card" aria-label="What happened">
        <strong>What happened</strong>
        <ul className="business-events">
          {business.events.map((event, index) => (
            <li key={`${event.at}-${index}`}>
              <span>
                {event.kind === 'takings' ? '🌙 Evening' : event.kind === 'bills' ? '🧾 Bills' : '•'} {event.text}
              </span>
              <small>{londonWhen(event.at)}</small>
              {event.amountPence > 0 && (
                <strong className={event.kind === 'takings' ? 'in' : undefined}>
                  {event.kind === 'takings' ? '+' : ''}
                  {formatPence(event.amountPence)}
                </strong>
              )}
            </li>
          ))}
        </ul>
      </section>
      <button
        type="button"
        className={`business-sell${selling ? ' sure' : ''}`}
        disabled={busy}
        onClick={async () => {
          if (!selling) return setSelling(true);
          if (await onAct({ action: 'sell', confirm: true }, true)) onSold();
        }}
      >
        {selling
          ? `Tap again to sell for ${formatPence(business.salePence)}${business.tillPence ? ` + the till` : ''}`
          : `Sell ${business.name}`}
      </button>
    </div>
  );
}
